import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'

import filter from 'lodash/filter'
import find from 'lodash/find'
import findFP from 'lodash/fp/find'
import flow from 'lodash/fp/flow'
import isArray from 'lodash/isArray'
import omitBy from 'lodash/omitBy'

import { useT } from '@galo-monitor/i18n'
import {
  cow,
  barley,
  briefcase,
  corn,
  dashboard,
  domain,
  flag,
  folderCog,
  folderOpen,
  list,
  plot,
  sack,
  silo,
  sprinkler,
  sprout,
  storage,
  task,
  tractor
} from '@galo-monitor/icons'
import { selectUser, selectUserApplications } from '@galo-monitor/stores/authentication/selectorAuthentication'
import { AVAILABLE_CONTEXTS } from '@galo-monitor/stores/context'
import { selectCurrentContext } from '@galo-monitor/stores/context/selectorContext'
import Icon from '@galo-monitor/web-components/Icon'

import DashboardScreen from '../screens/authenticated/common/DashboardScreen'
import UserProfileScreen from '../screens/authenticated/common/UserProfileScreen'
import ClientCompaniesScreen from '../screens/authenticated/company/ClientCompaniesScreen/ClientCompaniesScreen'
import CompanyConfigurationsScreen from '../screens/authenticated/company/CompanyConfiguration'
import FarmConfigurationsScreen from '../screens/authenticated/company/FarmConfiguration'
import FarmsScreen from '../screens/authenticated/company/FarmsScreen'
import OverviewScreen from '../screens/authenticated/company/OverviewScreen'
import ProjectsScreen from '../screens/authenticated/company/ProjectsScreen'
import SelectedFarmScreen from '../screens/authenticated/company/SelectedFarmScreen'
import UserFarmsScreen from '../screens/authenticated/company/UserFarmsScreen'
import FarmUsersScreen from '../screens/authenticated/farm/FarmUsersScreen'
import HarvestsScreen from '../screens/authenticated/farm/HarvestsScreen'
import MachinesScreen from '../screens/authenticated/farm/MachinesScreen'
import MapsScreen from '../screens/authenticated/farm/MapsScreen'
import PropertiesScreen from '../screens/authenticated/farm/PropertiesScreen'
import StocksScreen from '../screens/authenticated/farm/StocksScreen'
import ActivitiesScreen from '../screens/authenticated/property/ActivitiesScreen'
import ActivitiesProvider from '../screens/authenticated/property/ActivitiesScreen/context/Activities'
import BrandsModelsScreen from '../screens/authenticated/root/BrandsModelsScreen'
import BreedingsScreen from '../screens/authenticated/root/BreedingsScreen'
import CompaniesScreen from '../screens/authenticated/root/CompaniesScreen'
import CultivarsScreen from '../screens/authenticated/root/CultivarsScreen'
import CulturesScreen from '../screens/authenticated/root/CulturesScreen'
import FertilizersScreen from '../screens/authenticated/root/FertilizersScreen'
import FunctionalitiesScreen from '../screens/authenticated/root/FunctionalitiesScreen'
import ModulesScreen from '../screens/authenticated/root/ModulesScreen'
import PesticidesScreen from '../screens/authenticated/root/PesticidesScreen'
import SelectedCompanyScreen from '../screens/authenticated/root/SelectedCompanyScreen'

const checkIfResourceIsEnabled = (resource, applicationSlug) => {
  if (isArray(resource))
    return resource.includes(applicationSlug)

  return resource === applicationSlug
}

export const useRoutes = () => {
  const t = useT()

  const { isAdmin, isFarm } = useSelector(selectUser)
  const currentContext = useSelector(selectCurrentContext)
  const userApplications = useSelector(selectUserApplications)

  const appsEnabledByContext = useMemo(
    () => filter(
      userApplications,
      ({ contextSlug }) => contextSlug === currentContext
    ),
    [currentContext, userApplications]
  )

  const urlHasPermission = useCallback(
    (functionalitySlug, type) => !!find(
      userApplications,
      (application) => {
        switch(type) {
          case 'root':
            return checkIfResourceIsEnabled(functionalitySlug, application.nameSlug)
          default:
            return checkIfResourceIsEnabled(functionalitySlug, application.slug)
        }
      }
    ),
    [userApplications]
  )

  const getActionsBySlug = useCallback(
    (functionalitySlug) => {
      const permissions = flow(
        findFP(({ slug }) => slug === functionalitySlug)
      )(!isFarm ? appsEnabledByContext : userApplications)

      return {
        read: !isFarm || !!permissions.reader,
        write: !isFarm || !!permissions.created,
        edit: !isFarm || !!permissions.edited,
        delete: !isFarm || !!permissions.deleted
      }
    },
    [appsEnabledByContext, isFarm, userApplications]
  )

  // TODO: fix routes slugs set in routes objects
  const commonRoutes = useMemo(
    () => ({
      // pages like "notifications" or "user profile" can be here
      dashboard: {
        path: '/',
        slug: ['dashboard-farm', 'dashboard-viso-geral-meus-acessos-company'],
        exact: true,
        Component: DashboardScreen,
        title: t('dashboard'),
        menu: {
          group: 'general',
          Icon: props => <Icon icon={ dashboard } { ...props } />
        }
      },
      userProfile: {
        path: '/user/profile',
        exact: true,
        Component: UserProfileScreen,
        title: t('profile', { howMany: 1 })
      }
    }),
    [t]
  )

  const rootRoutes = useMemo(
    () => ({
      companies: {
        path: '/companies',
        exact: true,
        Component: CompaniesScreen,
        title: t('company', { howMany: 2 }),
        menu: {
          group: 'accounts',
          Icon: props => <Icon icon={ domain } { ...props } />
        }
      },
      selectedCompany: {
        path: '/companies/:companyId',
        Component: SelectedCompanyScreen
      },
      modules: {
        path: '/modules',
        exact: true,
        Component: ModulesScreen,
        title: t('module', { howMany: 2 }),
        menu: {
          group: 'root',
          Icon: props => <Icon icon={ folderOpen } { ...props } />
        }
      },
      functionalities: {
        path: '/functionalities',
        exact: true,
        Component: FunctionalitiesScreen,
        title: t('functionality', { howMany: 2 }),
        menu: {
          group: 'root',
          Icon: props => <Icon icon={ folderCog } { ...props } />
        }
      },
      breedings: {
        path: '/breedings',
        exact: true,
        Component: BreedingsScreen,
        title: t('breeding', { howMany: 2 }),
        menu: {
          group: 'database',
          Icon: props => <Icon icon={ cow } { ...props } />
        }
      },
      cultures: {
        path: '/cultures',
        exact: true,
        Component: CulturesScreen,
        title: t('culture', { howMany: 2 }),
        menu: {
          group: 'database',
          Icon: props => <Icon icon={ sprout } { ...props } />
        }
      },
      cultivars: {
        path: '/cultivars',
        Component: CultivarsScreen,
        title: t('cultivar', { howMany: 2 }),
        menu: {
          group: 'database',
          Icon: props => <Icon icon={ barley } { ...props } />
        }
      },
      pesticides: {
        path: '/pesticides',
        Component: PesticidesScreen,
        title: t('pesticide', { howMany: 2 }),
        menu: {
          group: 'database',
          Icon: props => <Icon icon={ sprinkler } { ...props } />
        }
      },
      fertilizers: {
        path: '/fertilizers',
        Component: FertilizersScreen,
        title: t('fertilizer', { howMany: 2 }),
        menu: {
          group: 'database',
          Icon: props => <Icon icon={ sack } { ...props } />
        }
      },
      brandsModels: {
        path: '/database',
        Component: BrandsModelsScreen,
        title: `${ t('brand', { howMany: 2 }) } ${ t('and') } ${ t('model', { howMany: 2 }) }`,
        menu: {
          group: 'database',
          Icon: props => <Icon icon={ tractor } { ...props } />
        }
      }
    }),
    [t]
  )

  const companyRoutes = useMemo(
    () => ({
      overview: {
        path: '/overview',
        slug: 'dashboard-viso-geral-meus-acessos-company',
        exact: true,
        Component: OverviewScreen,
        title: t('overview'),
        menu: {
          group: 'general',
          Icon: props => <Icon icon={ flag } { ...props } />
        }
      },
      userFarms: {
        path: '/farm-access',
        slug: 'dashboard-viso-geral-meus-acessos-company',
        exact: true,
        Component: UserFarmsScreen,
        title: t('my access', { howMany: 2 }),
        menu: {
          group: 'general',
          Icon: props => <Icon icon={ list } { ...props } />
        }
      },
      selectedFarm: {
        path: '/farms/:farmId',
        slug: 'gerenciamento-de-contas-farm',
        Component: SelectedFarmScreen
      },
      farms: {
        path: '/farms',
        slug: 'gerenciamento-de-contas-farm',
        exact: true,
        Component: FarmsScreen,
        title: `${ t('account', { howMany: 2 }) } Farm`,
        menu: {
          group: 'farm-admin',
          Icon: props => <Icon icon={ silo } { ...props } />
        }
      },
      farmSettings: {
        path: '/settings/farm',
        slug: ['gerenciamento-de-planos-farm', 'gerenciamento-de-papeis-fazenda-farm'],
        type: 'company',
        Component: FarmConfigurationsScreen,
        title: t('farm settings')
      },
      companySettings: {
        path: '/settings/company',
        slug: ['gerenciamento-de-usuarios-company', 'gerenciamento-de-papeis-company'],
        type: 'company',
        Component: CompanyConfigurationsScreen,
        title: t('company settings')
      },
      clientCompanies: {
        path: '/companies/clients',
        slug: 'gerenciamento-empresas-cliente-company',
        exact: true,
        Component: ClientCompaniesScreen,
        title: t('company client', { howMany: 2 }),
        menu: {
          group: 'services',
          Icon: props => <Icon icon={ domain } { ...props } />
        }
      },
      projects: {
        path: '/projects',
        slug: 'gerenciamento-de-projetos-company',
        exact: true,
        Component: ProjectsScreen,
        title: t('project', { howMany: 2 }),
        menu: {
          group: 'services',
          Icon: props => <Icon icon={ briefcase } { ...props } />
        }
      }
    }),
    [t]
  )

  const farmRoutes = useMemo(
    () => ({
      activities: {
        path: '/activities',
        slug: 'gerenciamento-de-atividades-farm',
        type: 'farm',
        exact: true,
        Component: () => (
          <ActivitiesProvider>
            <ActivitiesScreen />
          </ActivitiesProvider>
        ),
        title: t('activity', { howMany: 2 }),
        menu: {
          group: 'crop',
          Icon: props => <Icon icon={ task } { ...props } />
        }
      },
      harvests: {
        path: '/harvests',
        slug: 'gerenciamento-de-safras-farm',
        type: 'farm',
        exact: true,
        Component: HarvestsScreen,
        title: t('harvest', { howMany: 2 }),
        menu: {
          group: 'crop',
          Icon: props => <Icon icon={ corn } { ...props } />
        }
      },
      maps: {
        path: '/maps',
        slug: 'gerenciamento-de-mapas-farm',
        type: 'farm',
        Component: MapsScreen,
        title: t('map', { howMany: 2 }),
        menu: {
          group: 'inventory',
          Icon: props => <Icon icon={ plot } { ...props } />
        }
      },
      machines: {
        path: '/machines',
        slug: 'gerenciamento-de-maquinas-farm',
        type: 'farm',
        exact: true,
        Component: MachinesScreen,
        title: t('machine', { howMany: 2 }),
        menu: {
          group: 'inventory',
          Icon: props => <Icon icon={ tractor } { ...props } />
        }
      },
      rawMaterialStocks: {
        path: '/raw-materials-stocks',
        slug: 'gerenciamento-de-insumos-farm',
        type: 'farm',
        exact: true,
        Component: StocksScreen,
        title: t('raw material', { howMany: 2 }),
        menu: {
          group: 'inventory',
          Icon: props => <Icon icon={ storage } { ...props } />
        }
      },
      users: {
        path: '/users',
        slug: 'gerenciamento-de-usuarios-farm',
        type: 'farm',
        exact: true,
        Component: FarmUsersScreen,
        title: t('user', { howMany: 2 })
      },
      properties: {
        path: '/properties',
        slug: 'gerenciamento-de-propriedades-farm',
        type: 'farm',
        exact: true,
        Component: PropertiesScreen,
        title: t('property', { howMany: 2 })
      }
      // settings: {
      //   path: '/farm-settings',
      //   Component: FarmSettingsScreen,
      //   title: t('settings')
      // }
    }),
    [t]
  )

  const routes = useMemo(
    () => {
      const getRoutesByContext = () => {
        switch (currentContext) {
          case AVAILABLE_CONTEXTS.root: return rootRoutes
          case AVAILABLE_CONTEXTS.company: return companyRoutes
          case AVAILABLE_CONTEXTS.farm:
          case AVAILABLE_CONTEXTS.property:
            return farmRoutes
          default: return {}
          // TODO create case to each system module
        }
      }

      const enabledRoutes = getRoutesByContext()

      return isAdmin
        ? { ...commonRoutes, ...enabledRoutes }
        : {
          ...commonRoutes,
          ...omitBy(enabledRoutes,
            ({ slug, type }) => !urlHasPermission(slug, type)
          )
        }
    },
    [commonRoutes, companyRoutes, currentContext, farmRoutes, isAdmin, rootRoutes, urlHasPermission]
  )

  const menuGroups = useMemo(
    () => {
      switch(currentContext) {
        case AVAILABLE_CONTEXTS.root:
          return [
            { order: 0, group: 'general', title: '' },
            { order: 1, group: 'accounts', title: t('account', { howMany: 2 }) },
            { order: 2, group: 'database', title: t('database') },
            { order: 3, group: 'root', title: t('root') }
          ]
        case AVAILABLE_CONTEXTS.company:
          return [
            { order: 0, group: 'general', title: '' },
            { order: 1, group: 'farm-admin', title: 'farm admin' },
            { order: 2, group: 'services', title: 'services' }
          ]
        case AVAILABLE_CONTEXTS.farm:
        case AVAILABLE_CONTEXTS.property:
          return [
            { order: 0, group: 'general', title: '' },
            { order: 1, group: 'crop', title: t('crop') },
            { order: 2, group: 'inventory', title: t('inventory') }
          ]
        default: return null
      }
    },
    [currentContext, t]
  )

  return {
    routes,
    menuGroups,
    urlHasPermission,
    getActionsBySlug
  }
}
